import { BoardEditor, FlexHorizontal, IBoard, Spacer, createBoard } from '@vestaboard/installables'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React, { useState } from 'react'

import { Alert } from '@material-ui/lab'
import { useHistory } from 'react-router'
import { useListCountries } from '../hooks/useListCountries'

const DIMENSION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]

enum MemberRole {
  Owner = 'Owner',
  Admin = 'Admin',
  User = 'User'
}

interface IMember {
  email: string
  role: MemberRole
}

export interface ICreateDisplayGroupFormData {
  title: string
  rows: number
  columns: number
  timezoneId: string
  quietHoursBegin: string
  quietHoursEnd: string
  members: IMember[]
  firstMessageCharacters: IBoard | null
}

interface ICreateDisplayGroupFormProps {
  onSave: (data: ICreateDisplayGroupFormData) => Promise<void>
  saving?: boolean
}

const useStyles = makeStyles({
  card: {
    padding: 16,
    marginBottom: 24
  },
  sectionTitle: {
    paddingBottom: 12,
    paddingTop: 8
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 12
  },
  memberEmail: {
    flex: 2
  },
  memberRole: {
    flex: 1
  },
  boardEditorWrapper: {
    display: 'flex',
    justifyContent: 'center'
  }
})

export const CreateDisplayGroupForm = (props: ICreateDisplayGroupFormProps) => {
  const classes = useStyles()
  const history = useHistory()
  const { data: countriesData, loading: countriesLoading, error: countriesError } = useListCountries()

  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState<string | null>(null)
  const [rows, setRows] = useState(1)
  const [columns, setColumns] = useState(1)
  const [timezoneId, setTimezoneId] = useState('')
  const [quietHoursBegin, setQuietHoursBegin] = useState('')
  const [quietHoursEnd, setQuietHoursEnd] = useState('')
  const [members, setMembers] = useState<IMember[]>([])
  const [firstMessageCharacters, setFirstMessageCharacters] = useState<IBoard>(Array(3).fill(Array(15).fill(0)))
  const [includeFirstMessage, setIncludeFirstMessage] = useState(false)

  const isEmptyBoard = (board: IBoard): boolean => {
    return board.every(row => row.every(cell => cell === 0))
  }

  const handleAddMember = () => {
    setMembers([...members, { email: '', role: MemberRole.User }])
  }

  const handleRemoveMember = (index: number) => {
    setMembers(members.filter((_, i) => i !== index))
  }

  const handleMemberChange = (index: number, field: keyof IMember, value: string) => {
    const updated = [...members]
    updated[index] = { ...updated[index], [field]: value }
    setMembers(updated)
  }

  const handleSave = async () => {
    if (!title.trim()) {
      setTitleError('Title is required')
      return
    }
    setTitleError(null)

    await props.onSave({
      title: title.trim(),
      rows,
      columns,
      timezoneId,
      quietHoursBegin,
      quietHoursEnd,
      members,
      firstMessageCharacters:
        includeFirstMessage && !isEmptyBoard(firstMessageCharacters) ? firstMessageCharacters : null
    })
  }

  return (
    <>
      <Card className={classes.card}>
        <Typography variant='h6' className={classes.sectionTitle}>
          Basic Info
        </Typography>
        <TextField
          fullWidth
          label='Title'
          value={title}
          onChange={e => {
            setTitle(e.target.value)
            if (e.target.value.trim()) setTitleError(null)
          }}
          error={!!titleError}
          helperText={titleError}
          margin='normal'
          required
        />
      </Card>

      <Card className={classes.card}>
        <Typography variant='h6' className={classes.sectionTitle}>
          Dimensions
        </Typography>
        <Box display='flex' style={{ gap: 16 }}>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={rows} onChange={e => setRows(e.target.value as number)}>
              {DIMENSION_OPTIONS.map(n => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>Columns</InputLabel>
            <Select value={columns} onChange={e => setColumns(e.target.value as number)}>
              {DIMENSION_OPTIONS.map(n => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Spacer size='small' />
        <Typography variant='caption' color='textSecondary'>
          This will create {rows * columns} Vestaboard Note{rows * columns !== 1 ? 's' : ''} ({rows} row
          {rows !== 1 ? 's' : ''} × {columns} column{columns !== 1 ? 's' : ''})
        </Typography>
      </Card>

      <Card className={classes.card}>
        <Typography variant='h6' className={classes.sectionTitle}>
          Note Properties
        </Typography>
        {countriesError ? (
          <Alert severity='error'>Unable to load timezone options</Alert>
        ) : (
          <FormControl fullWidth margin='normal'>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={timezoneId}
              onChange={e => setTimezoneId(e.target.value as string)}
              disabled={countriesLoading}>
              <MenuItem value=''>
                <em>{countriesLoading ? 'Loading...' : 'None'}</em>
              </MenuItem>
              {countriesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                (countriesData?.countries || []).map(country =>
                  country.timezones.map(tz => (
                    <MenuItem key={tz.id} value={tz.id}>
                      {country.country} — {tz.timezoneCity}
                    </MenuItem>
                  ))
                )
              )}
            </Select>
          </FormControl>
        )}
        <Box display='flex' style={{ gap: 16 }}>
          <TextField
            label='Quiet Hours Begin'
            value={quietHoursBegin}
            onChange={e => setQuietHoursBegin(e.target.value)}
            placeholder='e.g. 2300'
            margin='normal'
            inputProps={{ maxLength: 4 }}
            style={{ flex: 1 }}
            helperText='4-digit time (e.g. 2300 for 11pm)'
          />
          <TextField
            label='Quiet Hours End'
            value={quietHoursEnd}
            onChange={e => setQuietHoursEnd(e.target.value)}
            placeholder='e.g. 0700'
            margin='normal'
            inputProps={{ maxLength: 4 }}
            style={{ flex: 1 }}
            helperText='4-digit time (e.g. 0700 for 7am)'
          />
        </Box>
      </Card>

      <Card className={classes.card}>
        <Typography variant='h6' className={classes.sectionTitle}>
          Members
        </Typography>
        {members.map((member, index) => (
          <Box key={index} className={classes.memberRow}>
            <TextField
              className={classes.memberEmail}
              label='Email Address'
              type='email'
              value={member.email}
              onChange={e => handleMemberChange(index, 'email', e.target.value)}
              placeholder='user@example.com'
              size='small'
            />
            <FormControl className={classes.memberRole} size='small'>
              <InputLabel>Role</InputLabel>
              <Select value={member.role} onChange={e => handleMemberChange(index, 'role', e.target.value as string)}>
                <MenuItem value={MemberRole.Owner}>Owner</MenuItem>
                <MenuItem value={MemberRole.Admin}>Admin</MenuItem>
                <MenuItem value={MemberRole.User}>User</MenuItem>
              </Select>
            </FormControl>
            <Button variant='outlined' size='small' color='secondary' onClick={() => handleRemoveMember(index)}>
              Remove
            </Button>
          </Box>
        ))}
        <Button variant='outlined' onClick={handleAddMember}>
          Add Member
        </Button>
        <Spacer size='small' />
        <Typography variant='caption' color='textSecondary'>
          If the person exists in the system, they will be added as a member. Otherwise, an invite will be sent.
        </Typography>
      </Card>

      <Card className={classes.card}>
        <Typography variant='h6' className={classes.sectionTitle}>
          First Message (Optional)
        </Typography>
        <Typography variant='body2' color='textSecondary' gutterBottom>
          Optionally send an initial message to all newly created Vestaboard Notes.
        </Typography>
        <Spacer size='small' />
        {!includeFirstMessage ? (
          <Button variant='outlined' onClick={() => setIncludeFirstMessage(true)}>
            Add First Message
          </Button>
        ) : (
          <>
            <Box className={classes.boardEditorWrapper}>
              <BoardEditor
                boardValue={firstMessageCharacters}
                setBoardValue={setFirstMessageCharacters}
                boardStyle='note-black'
                showFilledColor
              />
            </Box>
            <Spacer size='small' />
            <Button
              variant='outlined'
              size='small'
              onClick={() => {
                setIncludeFirstMessage(false)
                setFirstMessageCharacters(createBoard())
              }}>
              Remove First Message
            </Button>
          </>
        )}
      </Card>

      <FlexHorizontal spaceBetween>
        <Button variant='outlined' onClick={() => history.push('/note-arrays')} disabled={props.saving}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={handleSave} disabled={props.saving}>
          {props.saving ? <CircularProgress size={24} /> : 'Create Note Array'}
        </Button>
      </FlexHorizontal>
      <Spacer size='large' />
    </>
  )
}
